import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from './firebase';
import { collection, query, onSnapshot, doc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { useRole } from './RoleContext';
import { User, Printer } from 'lucide-react';
import kizunaLogo from './assets/kizuna-logo.png';

const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const getTodayString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = dateStr?.toDate ? dateStr.toDate() : new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, '0')}-${d.toLocaleString('en-US', { month: 'short' })}-${d.getFullYear()}`;
};

const formatMonthLabel = (monthStr) => {
  const [y, m] = (monthStr || '').split('-');
  if (!y || !m) return monthStr;
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

// Spells out an amount in words using the Bangladeshi lakh/crore grouping (standard
// for Taka figures on local payslips/cheques), e.g. 150000 -> "One Lakh Fifty Thousand".
const numberToWords = (num) => {
  num = Math.round(Number(num) || 0);
  if (num === 0) return 'Zero';
  const chunk = (n) => {
    let str = '';
    if (n >= 100) { str += ONES[Math.floor(n / 100)] + ' Hundred '; n %= 100; }
    if (n >= 20) { str += TENS[Math.floor(n / 10)] + ' '; n %= 10; }
    if (n > 0) str += ONES[n] + ' ';
    return str.trim();
  };
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  const rest = num;
  const parts = [];
  if (crore) parts.push(chunk(crore) + ' Crore');
  if (lakh) parts.push(chunk(lakh) + ' Lakh');
  if (thousand) parts.push(chunk(thousand) + ' Thousand');
  if (rest) parts.push(chunk(rest));
  return parts.join(' ');
};

// Same Bangladesh Labour Act, 2006 defaults used in HR.js -- kept in sync there.
const DEFAULT_LEAVE_QUOTA = { Sick: 14, Casual: 10, Earned: 16, Maternity: 112 };
const BASE_QUOTA_LEAVE_TYPES = ['Sick', 'Casual', 'Earned'];
const quotaTypesFor = (emp) => emp?.gender === 'Female' ? [...BASE_QUOTA_LEAVE_TYPES, 'Maternity'] : BASE_QUOTA_LEAVE_TYPES;
const leaveTypesFor = (emp) => emp?.gender === 'Female' ? ['Sick', 'Casual', 'Earned', 'Maternity', 'Unpaid'] : ['Sick', 'Casual', 'Earned', 'Unpaid'];

const daysBetween = (from, to) => {
  const a = new Date(from), b = new Date(to);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
};

const MyProfile = () => {
  const { employeeId, loading: roleLoading } = useRole();
  const [employee, setEmployee] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [month, setMonth] = useState(getCurrentMonth());
  const [leaveForm, setLeaveForm] = useState({ fromDate: getTodayString(), toDate: getTodayString(), type: 'Sick', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!employeeId) return;
    getDoc(doc(db, 'employees', employeeId)).then((snap) => {
      if (snap.exists()) setEmployee({ id: snap.id, ...snap.data() });
    });
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId) return;
    const unsub = onSnapshot(query(collection(db, 'payrollTransactions')), (snap) => {
      setTransactions(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((t) => t.employeeId === employeeId));
    });
    return () => unsub();
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId) return;
    const unsub = onSnapshot(query(collection(db, 'leaveRecords')), (snap) => {
      setLeaves(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((l) => l.employeeId === employeeId));
    });
    return () => unsub();
  }, [employeeId]);

  const salaryTxnsThisMonth = useMemo(
    () => transactions.filter((t) => t.type === 'salary' && t.month === month).sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)),
    [transactions, month]
  );
  const salaryPaidThisMonth = useMemo(
    () => salaryTxnsThisMonth.reduce((s, t) => s + Number(t.amount || 0), 0),
    [salaryTxnsThisMonth]
  );
  // The payslip's "Payment Date"/bank details reflect the most recent salary
  // payment this month -- if paid in two installments via different methods,
  // this shows the latest one.
  const lastSalaryPayment = salaryTxnsThisMonth[salaryTxnsThisMonth.length - 1];
  const advanceBalance = useMemo(
    () => transactions.filter((t) => t.type === 'advance').reduce((s, t) => s + Number(t.amount || 0), 0),
    [transactions]
  );
  const leaveBreakdown = useMemo(() => {
    const breakdown = {};
    quotaTypesFor(employee).forEach((type) => {
      const taken = leaves.filter((l) => l.type === type).reduce((s, l) => s + (l.days || 0), 0);
      const quota = Number(employee?.leaveQuota?.[type]) || DEFAULT_LEAVE_QUOTA[type];
      breakdown[type] = { taken, quota, remaining: Math.max(0, quota - taken) };
    });
    return breakdown;
  }, [leaves, employee]);

  const submitLeave = async () => {
    if (!employeeId || !employee) return;
    if (!leaveForm.fromDate || !leaveForm.toDate) return alert('Please pick both dates.');
    if (leaveForm.toDate < leaveForm.fromDate) return alert('"To" date cannot be before "From" date.');
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'leaveRecords'), {
        employeeId,
        employeeName: employee.name,
        fromDate: leaveForm.fromDate,
        toDate: leaveForm.toDate,
        days: daysBetween(leaveForm.fromDate, leaveForm.toDate),
        type: leaveForm.type,
        reason: leaveForm.reason.trim(),
        status: 'Pending',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || ''
      });
      setLeaveForm({ fromDate: getTodayString(), toDate: getTodayString(), type: 'Sick', reason: '' });
      alert('Leave request submitted -- waiting for Admin approval.');
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setSubmitting(false);
  };

  // window.print() on the live SPA page prints blank (the app's flex/100vh layout
  // doesn't translate to print media) -- same fix as Courier.js's label printing:
  // build a small self-contained HTML document in a new window instead.
  const printPayslip = () => {
    const method = lastSalaryPayment?.paymentMethod || 'Cash';
    // The print window's document is written from scratch (about:blank), so the logo
    // needs a fully-qualified URL -- a root-relative path wouldn't resolve there.
    const logoUrl = `${window.location.origin}${kizunaLogo}`;
    const win = window.open('', '_blank', 'width=520,height=700');
    if (!win) return alert('Pop-up is blocked -- please allow pop-ups for this site in your browser.');
    win.document.write(`
      <html><head><title>Payslip - ${employee.name} - ${month}</title>
      <style>
        body{font-family:sans-serif;margin:0;color:#333;}
        .card{border:1px solid #ddd;border-radius:10px;overflow:hidden;margin:20px;}
        .head{display:flex;align-items:center;border-bottom:2px solid #4a00e0;}
        .logo{width:64px;height:64px;border-radius:10px;object-fit:contain;margin:14px;}
        .head .co{flex:1;padding:14px 0;}
        .head .co h2{margin:0;color:#4a00e0;font-size:18px;} .head .co p{margin:2px 0 0;font-size:11px;color:#666;}
        .head .meta{padding:14px;text-align:right;}
        .head .meta p{margin:0;font-weight:bold;font-size:13px;} .head .meta span{font-size:9px;letter-spacing:1px;color:#999;}
        .info{display:flex;justify-content:space-between;padding:12px 18px;background:#f8f9ff;font-size:12px;}
        table{width:100%;border-collapse:collapse;font-size:13px;}
        th{background:#4a00e0;color:#fff;padding:8px 18px;text-align:left;}
        td{padding:8px 18px;}
        .alt{background:#fafafa;}
        .total-row td{border-top:2px solid #eee;font-weight:bold;}
        .foot{display:flex;justify-content:space-between;padding:14px 18px;gap:15px;flex-wrap:wrap;}
        .payinfo{font-size:12px;color:#555;line-height:1.9;}
        .netpay{text-align:center;background:#4a00e0;color:#fff;border-radius:10px;padding:12px 22px;min-width:150px;}
        .netpay p{margin:0;font-size:11px;opacity:0.85;} .netpay h2{margin:0;}
        .words{padding:0 18px 14px;font-size:11px;font-style:italic;color:#888;}
        .breakdown{padding:0 18px 14px;font-size:11px;color:#999;}
      </style></head><body>
        <div class="card">
          <div class="head">
            <img class="logo" src="${logoUrl}" alt="Kizuna" />
            <div class="co">
              <h2>PB KIZUNA COMPANY LIMITED</h2>
              <p>H-10, R-2, Section-2, Block-D, Aftabnagar, Dhaka-1212</p>
            </div>
            <div class="meta">
              <p>Payslip — ${formatMonthLabel(month)}</p>
              <span>CONFIDENTIAL</span>
            </div>
          </div>
          <div class="info">
            <div><b>Name:</b> ${employee.name}</div>
            <div><b>Designation:</b> ${employee.position || '-'} &nbsp;|&nbsp; <b>Department:</b> ${employee.department || '-'}</div>
          </div>
          <table>
            <tr><th>Description</th><th>Earnings</th><th>Deductions</th></tr>
            <tr><td>Basic Salary</td><td align="right">৳ ${formatTk(employee.monthlySalary)}</td><td></td></tr>
            <tr class="alt"><td>Transportation Allowance</td><td align="right">৳ 0</td><td></td></tr>
            <tr><td>Medical Allowance</td><td align="right">৳ 0</td><td></td></tr>
            <tr class="alt"><td>Tax</td><td></td><td align="right">৳ 0</td></tr>
            <tr class="total-row"><td>Total</td><td align="right">৳ ${formatTk(employee.monthlySalary)}</td><td align="right">৳ 0</td></tr>
          </table>
          <div class="foot">
            <div class="payinfo">
              <div><b>Payment Date:</b> ${lastSalaryPayment ? formatDate(lastSalaryPayment.createdAt) : '-'}</div>
              ${method === 'Bank' ? `
                <div><b>Bank Name:</b> ${employee.bankName || '-'}</div>
                <div><b>Account Name:</b> ${employee.bankAccountName || '-'}</div>
                <div><b>Account #:</b> ${employee.bankAccountNumber || '-'}</div>
              ` : method === 'bKash' ? `<div><b>bKash Number:</b> ${employee.bkashNumber || '-'}</div>` : `<div><b>Payment Method:</b> Cash</div>`}
            </div>
            <div class="netpay">
              <p>NET PAY</p>
              <h2>৳ ${formatTk(salaryPaidThisMonth)}</h2>
            </div>
          </div>
          <p class="words">In Words: Taka ${numberToWords(salaryPaidThisMonth)} Only</p>
          ${salaryTxnsThisMonth.length > 1 ? `<p class="breakdown">Paid in ${salaryTxnsThisMonth.length} installments: ${salaryTxnsThisMonth.map((t) => `৳${formatTk(t.amount)}`).join(' + ')}</p>` : ''}
        </div>
        <script>window.onload = () => window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  if (roleLoading) return null;

  if (!employeeId || !employee) {
    return (
      <div style={{ padding: '20px', background: '#f4f7fe', minHeight: '100vh' }}>
        <div className="white-box" style={{ padding: '40px', borderRadius: '25px', background: '#fff', textAlign: 'center', color: '#999' }}>
          <User size={40} style={{ marginBottom: '10px' }} />
          <h3>No employee profile linked to your account yet</h3>
          <p style={{ fontSize: '13px' }}>Ask your Admin to link your login to your employee record from HR → User Roles.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', background: '#f4f7fe', minHeight: '100vh' }}>
      <div className="white-box" style={{ padding: '30px', borderRadius: '25px', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
          {employee.photoUrl ? (
            <img src={employee.photoUrl} alt="" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#f4f7fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4a00e0' }}><User size={28} /></div>
          )}
          <div>
            <h2 style={{ margin: 0, color: '#333' }}>{employee.name}</h2>
            <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>{employee.position || '-'} {employee.department ? `· ${employee.department}` : ''}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '25px' }}>
          <div className="hover-lift" style={{ background: '#fff', padding: '20px', borderRadius: '20px', borderLeft: '6px solid #4a00e0', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: '200px', flex: 1 }}>
            <p style={{ margin: 0, color: '#666', fontSize: '13px', fontWeight: 'bold' }}>Monthly Salary</p>
            <h2 style={{ margin: 0, color: '#4a00e0', fontSize: '22px' }}>৳ {formatTk(employee.monthlySalary)}</h2>
          </div>
          <div className="hover-lift" style={{ background: '#fff', padding: '20px', borderRadius: '20px', borderLeft: '6px solid #e74c3c', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: '200px', flex: 1 }}>
            <p style={{ margin: 0, color: '#666', fontSize: '13px', fontWeight: 'bold' }}>Advance Balance (owed by you)</p>
            <h2 style={{ margin: 0, color: '#e74c3c', fontSize: '22px' }}>৳ {formatTk(advanceBalance)}</h2>
          </div>
        </div>

        <h3 style={{ marginBottom: '10px' }}>My Leave Balance</h3>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '25px' }}>
          {quotaTypesFor(employee).map((type) => {
            const b = leaveBreakdown[type];
            const pct = Math.min(100, (b.taken / b.quota) * 100);
            return (
              <div className="hover-lift" key={type} style={{ background: '#fff', padding: '18px', borderRadius: '18px', border: '1px solid #eee', minWidth: '180px', flex: 1 }}>
                <p style={{ margin: '0 0 6px', color: '#666', fontSize: '13px', fontWeight: 'bold' }}>{type} Leave</p>
                <div style={{ width: '100%', height: '8px', borderRadius: '5px', background: '#f0f0f0', overflow: 'hidden', marginBottom: '6px' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: b.taken >= b.quota ? '#e74c3c' : pct > 70 ? '#f59e0b' : '#10b981' }} />
                </div>
                <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>{b.taken} used of {b.quota} — <b style={{ color: '#333' }}>{b.remaining} left</b></p>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <h3 style={{ marginBottom: '10px' }}>My Payslip</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              <input type="month" className="input-field" value={month} onChange={(e) => setMonth(e.target.value)} style={{ flex: 1, marginBottom: 0, width: 'auto' }} />
              <button onClick={printPayslip} style={{ padding: '0 16px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}><Printer size={14} /> Print</button>
            </div>
            <div style={{ border: '1px solid #ddd', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #4a00e0' }}>
                <img src={kizunaLogo} alt="Kizuna" style={{ width: '64px', height: '64px', borderRadius: '10px', objectFit: 'contain', margin: '14px' }} />
                <div style={{ flex: 1, padding: '14px 0' }}>
                  <h3 style={{ margin: 0, color: '#4a00e0', fontSize: '16px' }}>PB KIZUNA COMPANY LIMITED</h3>
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#666' }}>H-10, R-2, Section-2, Block-D, Aftabnagar, Dhaka-1212</p>
                </div>
                <div style={{ padding: '14px', textAlign: 'right' }}>
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: '12px' }}>Payslip — {formatMonthLabel(month)}</p>
                  <span style={{ fontSize: '9px', letterSpacing: '1px', color: '#999' }}>CONFIDENTIAL</span>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 18px', background: '#f8f9ff', fontSize: '12px', flexWrap: 'wrap', gap: '6px' }}>
                <div><b>Name:</b> {employee.name}</div>
                <div><b>Designation:</b> {employee.position || '-'} &nbsp;|&nbsp; <b>Department:</b> {employee.department || '-'}</div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#4a00e0', color: '#fff' }}>
                    <th style={{ textAlign: 'left', padding: '8px 18px' }}>Description</th>
                    <th style={{ padding: '8px 18px' }}>Earnings</th>
                    <th style={{ padding: '8px 18px' }}>Deductions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td style={{ padding: '8px 18px' }}>Basic Salary</td><td align="right" style={{ padding: '8px 18px' }}>৳ {formatTk(employee.monthlySalary)}</td><td></td></tr>
                  <tr style={{ background: '#fafafa' }}><td style={{ padding: '8px 18px' }}>Transportation Allowance</td><td align="right" style={{ padding: '8px 18px' }}>৳ 0</td><td></td></tr>
                  <tr><td style={{ padding: '8px 18px' }}>Medical Allowance</td><td align="right" style={{ padding: '8px 18px' }}>৳ 0</td><td></td></tr>
                  <tr style={{ background: '#fafafa' }}><td style={{ padding: '8px 18px' }}>Tax</td><td></td><td align="right" style={{ padding: '8px 18px' }}>৳ 0</td></tr>
                  <tr style={{ borderTop: '2px solid #eee', fontWeight: 'bold' }}>
                    <td style={{ padding: '8px 18px' }}>Total</td>
                    <td align="right" style={{ padding: '8px 18px' }}>৳ {formatTk(employee.monthlySalary)}</td>
                    <td align="right" style={{ padding: '8px 18px' }}>৳ 0</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 18px', gap: '15px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '12px', color: '#555', lineHeight: 1.9 }}>
                  <div><b>Payment Date:</b> {lastSalaryPayment ? formatDate(lastSalaryPayment.createdAt) : '-'}</div>
                  {(lastSalaryPayment?.paymentMethod || 'Cash') === 'Bank' && (
                    <>
                      <div><b>Bank Name:</b> {employee.bankName || '-'}</div>
                      <div><b>Account Name:</b> {employee.bankAccountName || '-'}</div>
                      <div><b>Account #:</b> {employee.bankAccountNumber || '-'}</div>
                    </>
                  )}
                  {lastSalaryPayment?.paymentMethod === 'bKash' && <div><b>bKash Number:</b> {employee.bkashNumber || '-'}</div>}
                  {(!lastSalaryPayment || lastSalaryPayment.paymentMethod === 'Cash') && <div><b>Payment Method:</b> Cash</div>}
                </div>
                <div style={{ textAlign: 'center', background: '#4a00e0', color: '#fff', borderRadius: '10px', padding: '12px 22px', minWidth: '150px' }}>
                  <p style={{ margin: 0, fontSize: '11px', opacity: 0.85 }}>NET PAY</p>
                  <h2 style={{ margin: 0 }}>৳ {formatTk(salaryPaidThisMonth)}</h2>
                </div>
              </div>
              <p style={{ padding: '0 18px 14px', fontSize: '11px', fontStyle: 'italic', color: '#888' }}>In Words: Taka {numberToWords(salaryPaidThisMonth)} Only</p>
              {salaryTxnsThisMonth.length > 1 && (
                <p style={{ padding: '0 18px 14px', fontSize: '11px', color: '#999', marginTop: '-10px' }}>
                  Paid in {salaryTxnsThisMonth.length} installments: {salaryTxnsThisMonth.map((t) => `৳${formatTk(t.amount)}`).join(' + ')}
                </p>
              )}
            </div>
          </div>

          <div>
            <h3 style={{ marginBottom: '10px' }}>Apply for Leave</h3>
            <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>From</label>
            <input className="input-field" type="date" value={leaveForm.fromDate} onChange={(e) => setLeaveForm({ ...leaveForm, fromDate: e.target.value })} />
            <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>To</label>
            <input className="input-field" type="date" value={leaveForm.toDate} onChange={(e) => setLeaveForm({ ...leaveForm, toDate: e.target.value })} />
            <select className="input-field" value={leaveForm.type} onChange={(e) => setLeaveForm({ ...leaveForm, type: e.target.value })}>
              {leaveTypesFor(employee).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input className="input-field" placeholder="Reason" value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} />
            <button onClick={submitLeave} disabled={submitting} className="btn-confirm" style={{ width: '100%', padding: '12px', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Submitting...' : 'Submit Leave Request'}
            </button>
          </div>
        </div>

        <div style={{ marginTop: '25px' }}>
          <h3 style={{ marginBottom: '10px' }}>My Leave History</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                <th style={{ padding: '10px' }}>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {leaves.slice().sort((a, b) => (b.fromDate || '').localeCompare(a.fromDate || '')).map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                  <td style={{ padding: '10px' }}>{l.type}</td>
                  <td>{formatDate(l.fromDate)}</td>
                  <td>{formatDate(l.toDate)}</td>
                  <td>{l.days}</td>
                  <td>
                    <span style={{ padding: '3px 9px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', background: l.status === 'Approved' ? '#eafff3' : '#fff3cd', color: l.status === 'Approved' ? '#218c61' : '#8a6d3b' }}>
                      {l.status || 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
              {leaves.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No leave requests yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
